import { createSlice } from '@reduxjs/toolkit';
import { ScheduleConstraintCode } from 'services/api';
import type { ScheduleFilters } from 'shared/api';
import type {
  Schedule,
  ScheduleLesson,
  ScheduleErrorTimeslot,
  EducationClass,
  Teacher,
  ScheduleRoom,
} from 'services/api';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { ScheduleState, ScheduleTimeslotPatch } from './types';
import { findLessonById, hasLessonChanges } from '../utils';
import {
  getCurrentBellScheduleAction,
  getScheduleAction,
  getScheduleErrorsAction,
} from './actions';
import { scheduleSelectors } from './selectors';

const initialState: ScheduleState = {
  schedule: [],
  filters: {},
  scheduleIsLoading: false,
  selectedLesson: null,
  hasErrors: false,
  highlightedErrors: null,
  coloredLessons: false,
  constraints: [],
  violations: [],
  difficultyDistribution: null,
  bellSchedule: null,
  reportStatus: null,
  editMode: null,
  hideDifficultyDistributionConflicts: false,
  suggestions: null,
};

const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    setColoredLessons: (state, action: PayloadAction<boolean>) => {
      state.coloredLessons = action.payload;
    },
    setFilters: (state, action: PayloadAction<ScheduleFilters>) => {
      state.filters = action.payload;
    },
    setSchedule: (state, action: PayloadAction<Schedule[]>) => {
      state.schedule = action.payload;
    },
    setSelectedLesson: (
      state,
      action: PayloadAction<{
        lesson: ScheduleLesson;
        slotKey: string;
        errors?: ScheduleErrorTimeslot[];
        educationalClass?: EducationClass;
        detailOpen?: boolean;
      } | null>,
    ) => {
      state.selectedLesson = action.payload;
    },
    setHighlightedErrors: (state, action: PayloadAction<ScheduleErrorTimeslot[] | null>) => {
      state.highlightedErrors = action.payload;
    },
    setSuggestions: (state, action: PayloadAction<string[]>) => {
      state.suggestions = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = null;
    },
    applyDraftLessonPatch: (
      state,
      action: PayloadAction<{
        lessonId: number;
        patch: {
          pinned?: boolean;
          teacher?: Teacher;
          room?: ScheduleRoom;
          timeslot?: ScheduleTimeslotPatch;
        };
      }>,
    ) => {
      const { lessonId, patch } = action.payload;

      if (!state.editMode) return;

      const currentPlace = findLessonById(state.schedule, lessonId);
      const originalPlace = findLessonById(state.editMode.originalSchedule, lessonId);

      if (!currentPlace || !originalPlace) return;

      const { lesson: currentLesson, timeslot: currentTimeslot } = currentPlace;
      const {
        lesson: originalLesson,
        timeslot: originalTimeslot,
        scheduleItem: originalScheduleItem,
      } = originalPlace;

      if ('pinned' in patch) {
        currentLesson.pinned = patch.pinned;

        const isPinnedChange = originalLesson?.pinned !== currentLesson.pinned;

        if (isPinnedChange) {
          state.editMode.changesByLessonId[lessonId] = {
            ...state.editMode.changesByLessonId[lessonId],
            lessonId,
            pinned: currentLesson.pinned,
          };
        } else {
          delete state.editMode.changesByLessonId[lessonId]?.pinned;
        }
      }

      if (patch.teacher) {
        currentLesson.teacher = patch.teacher;

        const isTeacherChange = originalLesson.teacher.id !== currentLesson.teacher.id;

        if (isTeacherChange) {
          state.editMode.changesByLessonId[lessonId] = {
            ...state.editMode.changesByLessonId[lessonId],
            lessonId,
            teacherId: currentLesson.teacher.id,
          };
        } else {
          delete state.editMode.changesByLessonId[lessonId]?.teacherId;
        }
      }

      if (patch.room) {
        currentLesson.room = patch.room;

        const isRoomChange = originalLesson.room.id !== currentLesson.room.id;

        if (isRoomChange) {
          state.editMode.changesByLessonId[lessonId] = {
            ...state.editMode.changesByLessonId[lessonId],
            lessonId,
            roomId: currentLesson.room.id,
          };
        } else {
          delete state.editMode.changesByLessonId[lessonId]?.roomId;
        }
      }

      if (patch.timeslot) {
        const target = patch.timeslot;
        const targetScheduleItem = state.schedule.find(
          (scheduleItem) => scheduleItem.educationalClass.id === target.educationalClassId,
        );

        if (!targetScheduleItem) return;

        let targetTimeslot = targetScheduleItem.timeslots.find(
          (timeslot) =>
            timeslot.dayOfWeek === target.dayOfWeek &&
            timeslot.startTime === target.startTime &&
            timeslot.endTime === target.endTime,
        );

        if (!targetTimeslot) {
          targetScheduleItem.timeslots.push({
            id: Math.max(...targetScheduleItem.timeslots.map((timeslot) => timeslot.id), 0) + 1,
            dayOfWeek: target.dayOfWeek,
            startTime: target.startTime,
            endTime: target.endTime,
            pinned: false,
            lessons: [],
          });

          targetTimeslot = targetScheduleItem.timeslots[targetScheduleItem.timeslots.length - 1];
        }

        currentTimeslot.lessons = currentTimeslot.lessons.filter(
          (lessonItem) => lessonItem.id !== lessonId,
        );

        targetTimeslot.lessons.push(currentLesson);

        const isTimeslotChange =
          originalScheduleItem.educationalClass.id !== target.educationalClassId ||
          originalTimeslot.dayOfWeek !== target.dayOfWeek ||
          originalTimeslot.startTime !== target.startTime ||
          originalTimeslot.endTime !== target.endTime;

        if (isTimeslotChange) {
          state.editMode.changesByLessonId[lessonId] = {
            ...state.editMode.changesByLessonId[lessonId],
            lessonId,
            timeslot: target,
          };
        } else {
          delete state.editMode.changesByLessonId[lessonId]?.timeslot;
        }
      }

      if (!hasLessonChanges(state.editMode.changesByLessonId[lessonId])) {
        delete state.editMode.changesByLessonId[lessonId];
      }
    },
    enterEditMode: (state) => {
      state.editMode = {
        originalSchedule: state.schedule,
        changesByLessonId: {},
      };
    },
    cancelEditMode: (state) => {
      if (state.editMode) {
        state.schedule = state.editMode.originalSchedule;
      }
      state.editMode = null;
      state.suggestions = null;
    },
    finishEditMode: (state) => {
      state.editMode = null;
      state.suggestions = null;
    },
    toggleConstraintVisibility: (state, action: PayloadAction<ScheduleConstraintCode>) => {
      if (action.payload === ScheduleConstraintCode.DAILY_DIFFICULTY_DISTRIBUTION_CONFLICT) {
        state.hideDifficultyDistributionConflicts = !state.hideDifficultyDistributionConflicts;
      }

      state.constraints = state.constraints.map((constraint) => {
        if (constraint.code !== action.payload || constraint.mode === 'DISABLED') {
          return constraint;
        }

        const isVisible = !constraint.isVisible;

        return {
          ...constraint,
          isVisible,
          isHighlighted: isVisible && constraint.hasError,
        };
      });
    },
    clearState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Get schedule
    builder.addCase(getScheduleAction.pending, (state) => {
      const isInitialLoad = state.schedule.length === 0;
      state.scheduleIsLoading = isInitialLoad;
    });
    builder.addCase(getScheduleAction.fulfilled, (state, action) => {
      state.scheduleIsLoading = false;
      state.schedule = action.payload.items;
      state.hasErrors = !action.payload.score.perfect;
    });
    builder.addCase(getScheduleAction.rejected, (state) => {
      state.scheduleIsLoading = false;
    });
    // Get schedule errors
    builder.addCase(getScheduleErrorsAction.fulfilled, (state, action) => {
      state.difficultyDistribution = action.payload.difficultyDistribution;
      state.reportStatus = action.payload.status;
      state.violations = action.payload.violations;
      const errorCodes = new Set(state.violations.map((error) => error.code));
      state.constraints = action.payload.constraints.map((constraint) => {
        return {
          ...constraint,
          isVisible: constraint.mode === 'ENABLED',
          isDisabled: constraint.mode === 'DISABLED',
          hasError: errorCodes.has(constraint.code),
          isHighlighted: constraint.mode === 'ENABLED' && errorCodes.has(constraint.code),
        };
      });
    });
    // Get current bell schedule
    builder.addCase(getCurrentBellScheduleAction.fulfilled, (state, action) => {
      state.bellSchedule = action.payload;
    });
  },
});

export const scheduleActions = scheduleSlice.actions;
export const scheduleReducer = scheduleSlice.reducer;

export { scheduleSelectors };
